import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useSelector } from "react-redux";
import { debounce } from "lodash";
import { GetAdminInterviewChart } from "../../../../apiServices";
import { Dateyear, MonthDate } from "../../../../utility";

// Register necessary Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const AdminInterviewChart = () => {
  const LineFilter = useSelector(
    (state) => state.CandidateDashboardFilterDetails.CandidateBarFilter
  );

  // Data for the line chart
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Scheduled",
        data: [],
        borderColor: "#3d85c6",
        backgroundColor: "rgba(61, 133, 198, 0.10)",
        pointBackgroundColor: "#3d85c6",
        tension: 0.4, // Smooth lines
        fill: true, // Area fill
        pointRadius: 8, // Increase point radius
        pointHoverRadius: 10, // Increase hover radius
      },
      {
        label: "Attended",
        data: [],
        borderColor: "#169c50",
        backgroundColor: "rgba(22, 156, 80, 0.3)",
        pointBackgroundColor: "#169c50",
        tension: 0.4, // Smooth lines
        fill: true, // Area fill
        pointRadius: 8, // Increase point radius
        pointHoverRadius: 10, // Increase hover radius
      },
    ],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const getData = await GetAdminInterviewChart({ ...LineFilter });

        // Calculate totals for either weeks or months based on LineFilter.type
        const aggregatedData = getData.reduce((acc, entry) => {
          const {
            day_date,
            month_start,
            month_end,
            interview_date_count,
            is_attended_count,
          } = entry;

          let periodKey = "";
          if (["7days", "15days", "30days"].includes(LineFilter.type)) {
            // Use week range for weekly data
            periodKey = `${MonthDate(day_date)}`;
          } else if (
            ["3months", "6months", "12months"].includes(LineFilter.type)
          ) {
            // Use month range for monthly data
            periodKey = `${MonthDate(month_start)} - ${Dateyear(month_end)}`;
          }

          // Initialize the entry if it doesn't exist
          if (!acc[periodKey]) {
            acc[periodKey] = {
              interview_date_count: 0,
              is_attended_count: 0,
            };
          }

          // Sum up the counts for the selected period (week or month)
          acc[periodKey].interview_date_count += parseInt(
            interview_date_count,
            10
          );
          acc[periodKey].is_attended_count += parseInt(is_attended_count, 10);

          return acc;
        }, {});

        // Transform aggregated data into arrays for Chart.js
        const labels = Object.keys(aggregatedData);
        const scheduled = labels.map((label) =>
          aggregatedData[label].interview_date_count > 0
            ? aggregatedData[label].interview_date_count
            : 0
        );
        const attended = labels.map((label) =>
          aggregatedData[label].is_attended_count > 0
            ? aggregatedData[label].is_attended_count
            : 0
        );

        // Update chart data state
        setChartData({
          labels,
          datasets: [
            { ...chartData.datasets[0], data: scheduled },
            { ...chartData.datasets[1], data: attended },
          ],
        });
      } catch (error) {
        console.error("Error fetching chart data:", error);
      }
    };

    const debouncedFetchData = debounce(fetchData, 300);
    debouncedFetchData();

    return () => {
      debouncedFetchData.cancel();
    };
  }, [LineFilter]);

  // Chart options
  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "top", // Position of the legend
        labels: {
          usePointStyle: true,
        },
        onHover: function (event) {
          event.native.target.style.cursor = "pointer";
        },
      },
      title: {
        display: true,
        // text:
        //   LineFilter.type === "7days" ? "Weekly Overview" : "Monthly Overview", // Chart title
      },
      datalabels: {
        // Customize the data labels
        color: "#fff", // Set text color to white
        font: {
          weight: "bold", // Make the font bold
          size: 11, // Adjust the size of the font if necessary
        },
      },
    },

    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text:
            LineFilter.type === "12months"
              ? "Year"
              : ["7days", "30days", "15days"].includes(LineFilter.type)
              ? "Days"
              : "Months",
        },

        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true, // Ensures y-axis starts at 0
        ticks: {
          callback: function (value) {
            return Number.isInteger(value) ? value : null;
          },
        },
      },
    },
  };

  return (
    <div style={{ width: "70%", margin: "auto" }}>
      <Line data={chartData} options={options} />
    </div>
  );
};

export default AdminInterviewChart;
